import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/AuthContext";

/**
 * Shared gate for anything that requires an active membership (buying a
 * perk, following a referral link, etc). Call `requireActiveMembership()`
 * before proceeding — it returns true when clear to continue, or opens
 * the dialog and returns false when blocked. Render `<MembershipGateDialog />`
 * once, anywhere in the calling component's JSX.
 */
export function useMembershipGate() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const requireActiveMembership = () => {
        if (user?.membership_active) return true;
        setOpen(true);
        return false;
    };

    const MembershipGateDialog = () => (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent
                data-testid="membership-gate-dialog"
                className="max-w-md bg-white"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-xl text-navy-700">
                        Active membership required
                    </DialogTitle>
                    <DialogDescription>
                        You need an active membership to continue. Subscribe to
                        unlock member pricing on every deal.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        onClick={() => {
                            setOpen(false);
                            navigate("/member/subscriptions");
                        }}
                        data-testid="membership-gate-view-plans"
                        className="rounded-full bg-navy-700 hover:bg-navy-800 text-white"
                    >
                        View plans
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );

    return { requireActiveMembership, MembershipGateDialog };
}
